<script lang="ts">
    import { onMounted, reactive, ref, onUnmounted, inject } from 'vue';
    import vm, { exitToStartMenu, resetActiveJourney, saveCurrentJourney, UngappedJourney } from '@journeys/Journeys.viewmodel';

    // assets
    import { staticPresetsStart, staticPresetsActions, staticPresetsConditions } from './UserInterface/Menues/Partials/NodeStaticPresets';

    // components
    import UserInterface from './UserInterface/UserInterface.vue';
    import CanvasMain from './Canvas/CanvasMain.vue';

    import ModalContainer from '@shared/components/ModalContainer.vue';
    import NodePresetList from '@journeys/components/UserInterface/Menues/Partials/NodePresetList.vue';
    import ContextMenuContainer from '@shared/containers/ContextMenuContainer.vue';

    // !! to be depricated (replace by ContextMenuContainer that accepts menu component through event emit)
    import ContextMenu from '@shared/components/ContextMenu.vue';

    // utils
    import { debounce, requestFrame } from '@utils';
    import { HistoryClass } from '@shared/utils/IterationControllers';
    import { versionManager } from '@journeys/utils/VersionManager';
    import { cleanupOrphanedReferences } from '@journeys/utils/journeySerialization';
    import { api } from '@cwd/lib/main';
    import { JourneyStatus } from '@journeys/Enums';
    import { useRouter } from 'vue-router';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'JourneyEditor_Viewport',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const props = defineProps<{
        JourneyId: string;
    }>();

    const state = reactive<{
        hasLoaded: boolean;
    }>({
        hasLoaded: false,
    });

    const router = useRouter();
    const { viewport, nav, editor, activeJourney, account, toast } = vm;
    const viewportRef = ref<HTMLElement>();

    const handleKeyboardShortcuts = (ev: KeyboardEvent) => {
        if (!(document.activeElement === null || document.activeElement === document.body)) return;

        // zoom in
        if (ev.altKey && ev.key === '+') {
            ev.preventDefault();
            ev.stopPropagation();

            nav.z = nav.z + 100 > nav.zmin ? nav.zmin : nav.z + 100;
        }

        // zoom out
        if (ev.altKey && ev.key === '-') {
            ev.preventDefault();
            ev.stopPropagation();

            nav.z = nav.z - 100 < nav.zmax ? nav.zmax : nav.z - 100;
        }

        // delete
        if (ev.code === 'Delete' && document.activeElement === document.body) {
            ev.preventDefault();
            ev.stopPropagation();

            editor.events.emit('deleteselection');
        }

        // redo
        if (ev.ctrlKey && ev.code === 'KeyY' && document.activeElement === document.body) {
            ev.preventDefault();
            ev.stopPropagation();

            editor.events.emit('historyredo');
        }

        // undo
        if (ev.ctrlKey && ev.code === 'KeyZ' && document.activeElement === document.body) {
            ev.preventDefault();
            ev.stopPropagation();

            editor.events.emit('historyundo');
        }
    };

    const history = new HistoryClass<UngappedJourney>(activeJourney, {
        ignore: ['JourneyId', 'runtime', 'content', 'ownedObjects', 'scheduleController', 'steps', 'startingStep'],
        fullProps: ['points', 'Segments', 'Tags'],
        // events: editor.events,
    });

    const clearSelection = () => {
        editor.tools.sel_nodes = {};
        editor.tools.sel_points = {};
        editor.tools.sel_lines = {};
        editor.tools.sel_keyObj = null;
    };

    const handleHistoryStep = (step: ReturnType<typeof history.next>) => {
        if (!step) return;

        clearSelection();
        // step.deleted ? clearSelection() : editor.events.emit("calcselarea")

        for (let line of step.newLines) editor.events.emit('initline', line);

        debounce(
            () => {
                // debounce hitbox generation
                for (let id in activeJourney.lines) editor.events?.emit('newlinehitboxes', activeJourney.lines[id]);

                history.watcher?.muteUpdate();
            },
            {},
            1
        );
    };

    const { quickmenu, contextmenu } = viewport;
    const pauseTransition = ref(false);

    const openquickmenu = (ev: MouseEvent, val: any) => {
        pauseTransition.value = true;

        viewport.rect = viewport.el!.getBoundingClientRect();

        let evt = ev.target as HTMLElement;
        if (evt.dataset.nodetype === 'canvas-node') {
            contextmenu.show = val;
            contextmenu.x = ev.x - viewport.rect.x;
            contextmenu.y = ev.y - viewport.rect.y;
            contextmenu.nodeId = evt.dataset.id!;
        } else {
            quickmenu.show = val;
            quickmenu.x = ev.x - viewport.rect.x;
            quickmenu.y = ev.y - viewport.rect.y;
        }

        requestFrame(() => (pauseTransition.value = false));
    };

    // TODO: WIP replacement for quickmenu & contextmenu
    const opentContextMenu = (ev: MouseEvent, val: boolean) => {
        let evt = ev.target as HTMLElement;
        if (evt.dataset.nodetype === 'canvas-node') {
        } else {
        }
    };

    const setupListings = () => {
        // TODO: syntax....
        account.listings.add('/tags', { map_key: 'TagId', eager: true });
        account.listings.add('/textmessages/listing', { map_key: 'SmsMessageId', eager: true });
        account.listings.add('/lists', { map_key: 'ListId' });
        account.listings.add('/segments/listing', { map_key: 'SegmentId' });
        account.listings.add('/issues/listing', { map_key: 'IssueId' });
        account.listings.add('/surveys/listing', { map_key: 'SurveyId', eager: true });
        account.listings.add('/events/listing', { map_key: 'EventId' });
    };

    const mountJourney = () => {
        // Close any previously opened node menus and clear selections
        editor.state.showNodeMenu = false;
        editor.state.activeMenu = 'nodelists'; // Reset to default menu

        // Clear any existing node selections
        editor.tools.sel_nodes = {};
        editor.tools.sel_points = {};
        editor.tools.sel_lines = {};
        editor.tools.sel_keyObj = null;

        // Close any open menus in the viewport
        viewport.closeMenu('all');

        viewport.el = viewportRef.value;
        viewport.rect = viewport.el!.getBoundingClientRect();
        viewport.quickmenu.container = viewport.dims;
        viewport.quickmenu.containerRect = viewport.rect;
        viewport.contextmenu.container = viewport.dims;
        viewport.contextmenu.containerRect = viewport.rect;

        viewport.updateData = () => {
            viewport.dims.h = viewport.el!.offsetHeight;
            viewport.dims.w = viewport.el!.offsetWidth - (editor.state.showPropertyWindow ? viewport.computed.value.raw_pws : 0);
        };

        viewport.updateData();

        editor.events.addListener('clearselection', clearSelection);
        editor.events.addListener('historyredo', () => handleHistoryStep(history.next()));
        editor.events.addListener('historyundo', () => handleHistoryStep(history.prev()));
        editor.events.addListener('historyreset', () => history.reset());
        editor.events.addListener('savecurrent', () => saveCurrentJourney());
        editor.events.addListener('saveandexit', exitToStartMenu);

        window.addEventListener('resize', viewport.updateData);
        window.addEventListener('keydown', handleKeyboardShortcuts);

        if (!featureAccess.hasFeature('Automation.Journeys')) {
            toast.emit('note', {
                pos: 'top-left',
                type: 'error',
                message: {
                    head: $Resources.JourneyMissingFeatureHeading,
                    body: $Resources.JourneyMissingFeatureBody,
                    break: true,
                },
            });
        }
    };

    const mountJourneyAsync = async () => {
        activeJourney.JourneyId = Array.isArray(router.currentRoute.value.params.JourneyId)
            ? router.currentRoute.value.params.JourneyId[0]
            : router.currentRoute.value.params.JourneyId;
        activeJourney.Title = $Resources.CleanJourneyTitle;
        activeJourney.Status = JourneyStatus.Draft;

        const result = await api.get(`/journeys/${router.currentRoute.value.params.JourneyId}`);

        if (result) {
            const data = result.GraphicalData ? JSON.parse(result.GraphicalData) : activeJourney;
            const op_data = JSON.parse(result.OperationalData);
            const op_rootProps = ['JourneyId', 'steps', 'ownedObjects', 'startDateTime', 'scheduleController'];

            console.log('data', data);
            console.log('op_data', op_data);
            console.log('op_rootProps', op_rootProps);

            data.Status = result.Status;
            data.JourneyId = result.JourneyId; // Ensure JourneyId is set from result

            for (let key of op_rootProps) {
                if (op_data[key]) data[key] = op_data[key];
            }

            data.publishedStepNodeIds = new Set();
            for (let key in data.nodes) {
                if (op_data?.nodes && op_data.nodes[key]) {
                    Object.assign(data.nodes[key].data, op_data.nodes[key]);
                    if (op_data.nodes[key].SegmentId) data.publishedStepNodeIds.add(Number(key));
                }
                data.nodes[key].runtime ||= {};
            }

            data.Tags = result.Tags;
            data.AccessControlList = result.AccessControlList;
            data.CurrentUserAccessLevel = result.CurrentUserAccessLevel;
            data.AccountId = result.AccountId;
            data.CopiedFromAccountName = result.CopiedFromAccountName ?? null;

            Object.assign(vm.activeJourney, data);
            Object.assign(activeJourney, data);

            // Clean up any orphaned references (nodes referencing non-existent lines or vice versa)
            cleanupOrphanedReferences(activeJourney);
        } else {
            saveCurrentJourney(true);
        }

        vm.editor.init();
        setupListings();
        versionManager(activeJourney);
        editor.state.showStatsOverlay = false;
        state.hasLoaded = true;

        editor.persistantState.setup(vm.activeJourney.JourneyId!, {
            nav: {
                target: nav,
                watch_props: () => ({ x: nav.x, y: nav.y, z: nav.z }),
            },
        });
    };

    const unmountJourney = () => {
        editor.persistantState.disposeAll();
        history.dispose();
        editor.events.reset();

        window.removeEventListener('resize', viewport.updateData);
        window.removeEventListener('keydown', handleKeyboardShortcuts);

        resetActiveJourney();
    };

    onMounted(async () => {
        mountJourney();
        await mountJourneyAsync();
    });

    onUnmounted(() => {
        unmountJourney();
    });
</script>

<template>
    <section class="approot workspace">
        <div class="viewport" id="viewport" ref="viewportRef" data-nodetype="viewport">
            <CanvasMain v-if="viewport.el && state.hasLoaded" @click.right.stop.prevent="(ev: MouseEvent) => openquickmenu(ev, true)" />

            <!-- 
            <CanvasMain v-if="viewport.el" />
            -->

            <ModalContainer v-bind="quickmenu" title="Quickmenu">
                <div class="section-group" style="--index: 2">
                    <h3 class="h5">{{ $Resources.NodePresetsStartLabel }}</h3>
                    <NodePresetList :columns="4" :gap="8" :list="staticPresetsStart()" style="--index: 3" />
                </div>
                <div class="section-group mt-l" style="--index: 2">
                    <h3 class="h5">{{ $Resources.NodePresetsActionsLabel }}</h3>
                    <NodePresetList :columns="4" :gap="8" :list="staticPresetsActions()" style="--index: 3" />
                </div>
                <div class="section-group mt-l" style="--index: 2">
                    <h3 class="h5">{{ $Resources.NodePresetsConditionsLabel }}</h3>
                    <NodePresetList :columns="4" :gap="8" :list="staticPresetsConditions()" style="--index: 3" />
                </div>
            </ModalContainer>

            <ContextMenu v-bind="contextmenu" />

            <ContextMenuContainer :eventController="editor.events" />

            <!--
            DEBUG NAV
            <div style="position: absolute; bottom: 30px; left: 0; z-index: 1000">
                <input type="number" v-model="nav.x" />
                <input type="number" v-model="nav.y" />
                <input type="number" v-model="nav.z" />
            </div>
            -->
        </div>
        <!-- Sidebar -->
        <UserInterface />
    </section>
</template>

<style lang="less">
    body:has(.workspace) {
        overflow: hidden;
    }

    .workspace {
        display: grid;
        position: relative;
        grid-template-columns: auto var(--editor-edit-pane-nav-width);
        top: 0;
        left: 0;
        height: 100vh;
        will-change: width, height;
        contain: content;
    }

    .viewport {
        display: block;
        position: relative;
        height: 100%;
        width: 100%;
        flex-grow: 0;
        flex-shrink: 0;
        background-color: #f5f5f5;
        background: -webkit-radial-gradient(circle at 50% 50%, #f7f7f7 70%, #eeeeee);
        background: radial-gradient(circle at 50% 50%, #f7f7f7 70%, #eeeeee);
        perspective: 1000px; /* set this with js in vp settings */

        will-change: width, height;
        contain: content;
    }

    .scr-xy {
        overflow: auto;
    }
</style>
